/**
 * useCachedResource — one of the reference-list requests a page needs on mount,
 * served from `requestCache` so returning to the page does not refetch it.
 *
 * Replaces the shape these call sites all had by hand:
 *
 *     const [value, setValue] = useState(fallback);
 *     useEffect(() => { api.load().then(setValue).catch(() => setValue(fallback)); }, []);
 *
 * which refetched on every mount and had no cancellation. Kept deliberately
 * small: no loading flag, no error object, no retry — none of the call sites
 * used them, and a failed load falls back to the empty list exactly as before.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cachedRequest, invalidateRequest } from '@/utils/admin/requestCache';

interface Options {
  /** When false the request is not made at all — for data only a closed drawer
   *  or dialog needs. Flipping it to true later loads on the spot. */
  enabled?: boolean;
  ttlMs?: number;
}

/**
 * Returns the value (or `fallback` until it arrives / if it fails) and a
 * `refresh` that drops the cache entry and reloads — call it after a mutation
 * that changes what the endpoint returns, otherwise the cache would keep
 * serving the pre-mutation list.
 */
export function useCachedResource<T>(
  key: string,
  load: () => Promise<T>,
  fallback: T,
  { enabled = true, ttlMs }: Options = {}
): [T, () => void] {
  const [value, setValue] = useState<T>(fallback);

  // Latest-ref so the effect can depend on `key`/`enabled` alone. Callers pass
  // inline closures, which would otherwise change identity every render and
  // refire the request on each one — the exact opposite of the point.
  const loadRef = useRef(load);
  loadRef.current = load;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const run = useCallback(() => {
    let ignore = false;
    cachedRequest(key, () => loadRef.current(), ttlMs)
      .then((next) => {
        if (!ignore) setValue(next as T);
      })
      .catch(() => {
        if (!ignore) setValue(fallbackRef.current);
      });
    return () => {
      ignore = true;
    };
  }, [key, ttlMs]);

  useEffect(() => {
    if (!enabled) return undefined;
    return run();
  }, [enabled, run]);

  const refresh = useCallback(() => {
    invalidateRequest(key);
    run();
  }, [key, run]);

  return [value, refresh];
}
